import { useCallback, useEffect, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import { useSpace } from "@/context/SpaceContext";
import { useFeed } from "@/hooks/useFeed";
import { LibraryView } from "@/components/library/LibraryView";
import { FilterSheet } from "@/components/library/FilterSheet";

const saved = { sort: "taken_desc", filters: {} }; // survives tab switches within the session

export default function LibraryPage() {
  const { space, version } = useSpace();
  const [sort, setSortState] = useState(saved.sort);
  const [filters, setFiltersState] = useState(saved.filters);
  const [open, setOpen] = useState(false);
  const setSort = (s) => { saved.sort = s; setSortState(s); };
  const setFilters = (f) => { saved.filters = f; setFiltersState(f); };
  const { vis, ...rest } = filters;
  const fetchPage = useCallback(
    (cursor) => api.get("/items", { params: { space: vis || space, sort, cursor, ...rest, _v: version } }).then((r) => r.data),
    [space, sort, filters, version], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const feed = useFeed(fetchPage, { cacheKey: `lib|${space}|${sort}|${JSON.stringify(filters)}|${version}` });
  const { restoredScrollY } = feed;
  useEffect(() => {
    if (!restoredScrollY) return undefined;
    let tries = 0;
    let raf;
    const go = () => {
      if (document.documentElement.scrollHeight >= restoredScrollY + window.innerHeight || tries > 30) window.scrollTo(0, restoredScrollY);
      else { tries += 1; raf = requestAnimationFrame(go); }
    };
    raf = requestAnimationFrame(go);
    return () => cancelAnimationFrame(raf);
  }, [restoredScrollY]);
  const active = Object.values(filters).filter(Boolean).length;

  return (
    <div data-testid="library-page">
      <LibraryView
        feed={feed}
        space={space}
        timeline={sort.startsWith("taken")}
        empty={active ? "Nothing matches these filters." : "No photos yet — tap the upload icon to add some."}
        toolbar={(
          <button className={`tool-btn ${active ? "on" : ""}`} onClick={() => setOpen(true)} data-testid="open-filters-button">
            <SlidersHorizontal size={16} /> <span>{active ? `Filters · ${active}` : "Sort & filter"}</span>
          </button>
        )}
      />
      <FilterSheet open={open} onOpenChange={setOpen} space={space} version={version} sort={sort} onSort={setSort} filters={filters} onFilters={setFilters} />
    </div>
  );
}
